import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { clearAuthTokens } from '@/lib/api';
import { Button } from '@/components/ui/button';
import NotificationCenter from '@/components/NotificationCenter';
import PunchClockButton from '@/components/PunchClockButton';
import ResponsiveNav from '@/components/ResponsiveNav';
import Breadcrumb from '@/components/Breadcrumb';
import { Home as HomeIcon } from 'lucide-react';
import logo from '@/assets/logo.png';

function Layout({ children, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMcpOwner, setIsMcpOwner] = useState(false);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      api.get('/mcp-consent/is-owner').then((r) => setIsMcpOwner(!!r.data?.is_owner)).catch(() => {});
    }
  }, [user?.role]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      clearAuthTokens();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthTokens();
      navigate('/login');
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isClient = user?.role?.toUpperCase() === 'CLIENT';
  const homePath = isClient ? '/meus-projetos' : '/home';

  // Client-specific simplified menu
  const clientNavItems = [
    { path: '/meus-projetos', label: 'Meus Projetos', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
  ];

  // Base items for ADMIN/CONSULTANT users
  const navItems = isClient ? clientNavItems : [
    { path: '/task-calendar', label: 'Atividades', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { path: '/deadline-control', label: 'Prazos', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { path: '/projects', label: 'Projetos', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
  ];

  // Add Analytics, Evolução and Desempenho for ADMIN and CONSULTANT
  if (!isClient && ['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase())) {
    navItems.push({ path: '/analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' });
    navItems.push({ path: '/evolution', label: 'Evolução', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' });
    navItems.push({ path: '/time-tracking', label: 'Horas', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' });
    navItems.push({ path: '/desempenho', label: 'Desempenho', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' });
  }

  // "Meu Ponto" nav item for CONSULTANT (points to /users where they see only the Ponto tab)
  if (user?.role === 'CONSULTANT') {
    navItems.push({ path: '/users', label: 'Meu Ponto', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' });
  }

  // ADMIN only items
  if (user?.role === 'ADMIN') {
    navItems.push({ path: '/prioridades', label: 'Prioridades', icon: 'M13 10V3L4 14h7v7l9-11h-7z' });
    navItems.push({ path: '/clients', label: 'Clientes', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' });
    navItems.push({ path: '/financial', label: 'Financeiro', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' });
    navItems.push({ path: '/budgets', label: 'Orçamentos', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' });
    navItems.push({ path: '/reports', label: 'Relatórios', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' });
    navItems.push({ path: '/project-templates', label: 'Templates', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' });
    navItems.push({ path: '/users', label: 'Usuários', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' });
    navItems.push({ path: '/notifications', label: 'Notificações', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' });
    navItems.push({ path: '/diagnostics', label: 'Coerência', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' });
    navItems.push({ path: '/health', label: 'Diagnóstico', icon: 'M5 12l5 5L20 7' });
  }

  // MCP (Claude connector) admin-only pages — only for the single allowed owner
  if (user?.role === 'ADMIN' && isMcpOwner) {
    navItems.push({ path: '/mcp', label: 'Conector Claude', icon: 'M13 10V3L4 14h7v7l9-11h-7z' });
  }

  // Whitelist override: specific consultants who also get Templates access
  const TEMPLATES_WHITELIST = ['jacira@quatrofolhas.com.br'];
  if (user?.role === 'CONSULTANT' && TEMPLATES_WHITELIST.includes((user?.email || '').toLowerCase())) {
    navItems.push({ path: '/project-templates', label: 'Templates', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' });
  }

  // Sort all items alphabetically by label (only for non-client menus)
  if (!isClient) {
    navItems.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Primeira linha: Logo e Ações do usuário */}
          <div className="flex justify-between items-center h-20 border-b border-gray-100">
            <Link
              to={homePath}
              className="flex items-center group focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 rounded-md transition-opacity hover:opacity-80"
              aria-label="Voltar para a página inicial"
              data-testid="header-logo-link"
            >
              <img src={logo} alt="Quatro Folhas - Soluções ambientais desde 1992" className="h-16 w-auto" />
            </Link>

            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate(homePath)}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
                aria-label="Página inicial"
                title="Página inicial"
                data-testid="header-home-button"
              >
                <HomeIcon className="w-5 h-5" />
              </Button>

              <NotificationCenter />

              <PunchClockButton />

              <div className="hidden md:flex items-center space-x-3">
                {user?.picture ? (
                  <img src={user.picture} alt={user.full_name} className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 bg-emerald-800 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">{user?.full_name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                )}
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900" data-testid="header-user-name">{user?.full_name || '—'}</p>
                  <p className="text-xs text-gray-500">
                    {user?.role === 'ADMIN' ? 'Administrador' : user?.role === 'CONSULTANT' ? 'Consultor' : 'Cliente'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/meu-cadastro')}
                variant="outline"
                size="sm"
                className="border-gray-300"
                data-testid="my-profile-button"
              >
                Meu Cadastro
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-gray-300"
                data-testid="logout-button"
              >
                Sair
              </Button>
              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Segunda linha: Navegação (com overflow dropdown "Mais" quando necessário) */}
          <div className="hidden md:block">
            <ResponsiveNav items={navItems} isActive={isActive} />
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-lg ${
                    isActive(item.path)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-[calc(100vh-220px)]">
        <Breadcrumb user={user} />
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs text-gray-500">
            Desenvolvido por Castle Green.
          </p>
        </div>
      </footer>

    </div>
  );
}

export default Layout;